"use client";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { getCdnImagePath } from "@/utils/bus/getCdnImagePath";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import Image from "next/image";

// analytics
import { pushCtaClickDataLayer } from "@/utils/pushCtaClickDataLayer";

export default function RegisterBanner({
  translationNamespace = "common",
  mobileBackgroundUrl = "/saka/common/register-mobile.png",
  desktopImageUrl = "/saka/common/registerbanner-new.png",
  wrapperClassName = "",
  dataLayerPayload = null,
}) {
  const { isLogin, showLoginModal } = useAuth();
  const t = useTranslationDetector(translationNamespace);

  const handleClick = () => {
    if (dataLayerPayload) {
      pushCtaClickDataLayer(dataLayerPayload, "sign_up_free");
    }
    showLoginModal();
  };

  if (isLogin) {
    return null;
  }

  return (
    <div className="container mx-auto">
      <div
        className={clsx(
          "relative flex w-full flex-col items-start justify-between overflow-hidden rounded-lg p-6 text-left md:aspect-auto md:h-[400px] md:flex-row md:items-center md:p-0",
          wrapperClassName,
        )}
      >
        {/* Background images via responsive Next/Image */}
        <div className="absolute inset-0 z-0 h-full w-full rtl:scale-x-[-1]">
          {/* Responsive background via <picture> to avoid double loading */}
          <picture className="absolute inset-0 h-full w-full">
            <source
              media="(min-width: 768px)"
              srcSet={getCdnImagePath(desktopImageUrl)}
            />
            <Image
              src={getCdnImagePath(mobileBackgroundUrl)}
              alt="registerbanner-bg"
              fill
              priority={false}
              className="object-cover"
            />
          </picture>
          {/* Gradient overlays (responsive) */}
          <div
            className="absolute inset-0 block md:hidden"
            style={{
              background:
                "linear-gradient(357deg, rgba(245,245,249,0) 16.64%, #F5F5F9 101.04%), linear-gradient(93deg, #F5F5F9 16.43%, rgba(245,245,249,0) 80.86%)",
            }}
          />
          <div
            className="absolute inset-0 hidden md:block"
            style={{
              background:
                "linear-gradient(87deg, #F5F5F9 41.17%, rgba(245,245,249,0) 87.64%)",
            }}
          />
        </div>
        <div className="z-10 flex h-full w-full flex-col items-start justify-between gap-30 md:gap-4 md:p-10 lg:gap-10 ltr:md:pr-0 ltr:lg:p-15 ltr:lg:pr-0 rtl:md:pl-0 rtl:lg:p-15 rtl:lg:pl-0">
          {" "}
          <div className="flex flex-col gap-6">
            <h2 className="text-base-custom text-foreground m-0 font-semibold lg:text-4xl">
              {t("registerBannerTitle")}
            </h2>
            <ul className="m-o items-left flex flex-col justify-center gap-4 p-0">
              <li className="flex flex-row items-center justify-start gap-1">
                <FontAwesomeIcon
                  className="h-6 w-6"
                  icon={byPrefixAndName.far["circle-check"]}
                />
                <p className="text-foreground text-lg">
                  {t("registerBannerBenefit1")}
                </p>
              </li>
              <li className="flex flex-row items-center justify-start gap-1">
                <FontAwesomeIcon
                  className="h-6 w-6"
                  icon={byPrefixAndName.far["circle-check"]}
                />
                <p className="text-foreground text-lg">
                  {t("registerBannerBenefit2")}
                </p>
              </li>
              <li className="flex flex-row items-center justify-start gap-1">
                <FontAwesomeIcon
                  className="h-6 w-6"
                  icon={byPrefixAndName.far["circle-check"]}
                />
                <p className="text-foreground text-lg">
                  {t("registerBannerBenefit3")}
                </p>
              </li>
            </ul>
          </div>
          <Button
            className="flex h-[50px] flex-row items-center gap-2"
            onClick={handleClick}
          >
            <span className="text-base lg:text-lg">
              {t("registerBannerCta")}
            </span>
            <FontAwesomeIcon
              className="h-6 w-6"
              icon={byPrefixAndName.far["arrow-right"]}
            />
          </Button>
        </div>
      </div>
    </div>
  );
}
